import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Trash2, Power } from "lucide-react";
import { TYPE_META } from "./GuestCard";

const TYPES = [["large", "Large Gazebo"], ["medium", "Medium Gazebo"], ["small", "Small Gazebo"], ["umbrella", "Umbrella"]];

export const EditSpotDialog = ({ spot, onOpenChange, onSave, onDelete }) => {
  const open = !!spot;
  const [type, setType] = useState("small");
  const [num, setNum] = useState("");
  const [capacity, setCapacity] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (spot) {
      setType(spot.type);
      setNum(String(spot.num ?? ""));
      setCapacity(String(spot.capacity ?? ""));
      setDescription(spot.description || "");
      setActive(spot.active !== false);
      setConfirmDel(false);
    }
  }, [spot]);

  if (!spot) return null;

  const save = () => {
    onSave(spot.id, {
      type,
      num: num !== "" ? Number(num) : undefined,
      capacity: capacity !== "" ? Number(capacity) : undefined,
      description,
      active,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="edit-spot-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-wide">Edit spot</DialogTitle>
          <DialogDescription className="sr-only">Change this spot's number, type, capacity or description, or delete it.</DialogDescription>
        </DialogHeader>

        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</p>
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map(([v, l]) => (
              <button key={v} data-testid={`edit-type-${v}`} onClick={() => setType(v)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${type === v ? "border-slate-900 bg-slate-900 text-white" : "border-sand-300 bg-white text-slate-600 hover:border-slate-400"}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${TYPE_META[v].dot}`} /> {l}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Number</p>
            <Input data-testid="edit-num-input" value={num}
              onChange={(e) => setNum(e.target.value.replace(/\D/g, ""))} className="h-9" />
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Capacity (pax)</p>
            <Input data-testid="edit-capacity-input" value={capacity}
              onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ""))} className="h-9" />
          </div>
        </div>

        <div className="mt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Description (optional)</p>
          <Input data-testid="edit-desc-input" value={description}
            onChange={(e) => setDescription(e.target.value)} placeholder="e.g. corner spot near the bar" className="h-9" />
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl border border-sand-300 bg-white px-3 py-2.5">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Power size={14} /> In service</p>
            <p className="text-[11px] leading-snug text-slate-500">
              {active ? "Available for allocation." : "Out of service — excluded from auto & manual allocation."}
            </p>
          </div>
          <button
            data-testid="edit-active-toggle"
            role="switch"
            aria-checked={active}
            onClick={() => setActive((v) => !v)}
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${active ? "bg-sea" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${active ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2">
          {confirmDel ? (
            <button data-testid="edit-delete-confirm" onClick={() => { onDelete(spot.id); onOpenChange(false); }}
              className="h-10 flex-1 rounded-lg bg-destructive text-sm font-semibold text-white hover:bg-destructive/90">
              Confirm delete
            </button>
          ) : (
            <button data-testid="edit-delete-btn" onClick={() => setConfirmDel(true)}
              className="flex h-10 shrink-0 items-center justify-center gap-1 rounded-lg bg-white px-3 text-sm font-semibold text-destructive ring-1 ring-destructive/40 hover:bg-destructive/10">
              <Trash2 size={15} /> Delete
            </button>
          )}
          <button data-testid="edit-save-btn" onClick={save}
            className="h-10 flex-1 rounded-lg bg-brand text-sm font-semibold text-white hover:bg-brand-600">
            Save changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
